import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  getActiveFastingSession,
  startFastingSession,
  stopFastingSession,
  completeFastingSession,
  getFastingSettings,
  updateFastingSettings,
  getFastingHistory,
  type FastingSession,
  type FastingSettings,
} from '@/lib/firebase/queries/fasting';
import { onSnapshot, collection, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';

interface UseFastingReturn {
  session: FastingSession | null;
  settings: FastingSettings | null;
  elapsedHours: number;
  elapsedMinutes: number;
  elapsedSeconds: number;
  progress: number; // 0-100
  isFasting: boolean;
  loading: boolean;
  lastBreakTime: Date | null; // Last time the user broke their fast
  eatingWindowStartTime: Date | null; // When the eating window started (after last break)
  eatingWindowHours: number; // Hours in eating window (24 - fasting hours)
  eatingElapsedHours: number;
  eatingElapsedMinutes: number;
  eatingElapsedSeconds: number;
  eatingProgress: number; // 0-100
  startFasting: (targetHours?: number) => Promise<void>;
  stopFasting: () => Promise<void>;
  updateSettings: (settings: Partial<Omit<FastingSettings, 'userId' | 'updatedAt'>>) => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Hook to manage fasting sessions with real-time updates
 */
export function useFasting(): UseFastingReturn {
  const { user } = useAuth();
  const [session, setSession] = useState<FastingSession | null>(null);
  const [settings, setSettings] = useState<FastingSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [elapsedTime, setElapsedTime] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [eatingElapsedTime, setEatingElapsedTime] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [lastBreakTime, setLastBreakTime] = useState<Date | null>(null);
  const [eatingWindowStartTime, setEatingWindowStartTime] = useState<Date | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Calculate elapsed time from start time
  const calculateElapsedTime = useCallback((startTime: Date): { hours: number; minutes: number; seconds: number } => {
    const now = new Date();
    const diff = now.getTime() - startTime.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    return { hours, minutes, seconds };
  }, []);

  // Calculate eating window hours from fasting plan
  const eatingWindowHours = useMemo(() => {
    if (!settings?.defaultPlan) return 8; // Default 16:8 = 8 hours eating
    const fastingHours = parseFastingPlan(settings.defaultPlan);
    return 24 - fastingHours;
  }, [settings?.defaultPlan]);

  // Update eating window start time when last break time changes
  useEffect(() => {
    const isCurrentlyFasting = session?.status === 'active' || false;
    if (lastBreakTime && !isCurrentlyFasting) {
      setEatingWindowStartTime(lastBreakTime);
    } else if (isCurrentlyFasting) {
      setEatingWindowStartTime(null);
      setEatingElapsedTime({ hours: 0, minutes: 0, seconds: 0 });
    }
  }, [lastBreakTime, session]);

  // Update elapsed time every second
  useEffect(() => {
    if (session && session.status === 'active') {
      // Update immediately
      setElapsedTime(calculateElapsedTime(session.startTime));

      // Update every second
      intervalRef.current = setInterval(() => {
        setElapsedTime(calculateElapsedTime(session.startTime));
        
        // Auto-complete if target reached
        if (session.targetHours > 0) {
          const elapsedHours = calculateElapsedTime(session.startTime).hours + 
                               calculateElapsedTime(session.startTime).minutes / 60;
          if (elapsedHours >= session.targetHours && user) {
            completeFastingSession(user.uid).catch(console.error);
          }
        }
      }, 1000);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    } else {
      setElapsedTime({ hours: 0, minutes: 0, seconds: 0 });
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
  }, [session, calculateElapsedTime, user]);

  // Update eating window elapsed time every second (stop if exceeds 24 hours)
  useEffect(() => {
    const isCurrentlyFasting = session?.status === 'active' || false;
    if (eatingWindowStartTime && !isCurrentlyFasting) {
      const elapsed = calculateElapsedTime(eatingWindowStartTime);
      
      // Stop tracking if elapsed time exceeds 24 hours
      if (elapsed.hours >= 24) {
        setEatingElapsedTime({ hours: 0, minutes: 0, seconds: 0 });
        setEatingWindowStartTime(null);
        return;
      }

      // Update immediately
      setEatingElapsedTime(elapsed);

      // Update every second
      const eatingInterval = setInterval(() => {
        const currentElapsed = calculateElapsedTime(eatingWindowStartTime);
        
        // Stop tracking if elapsed time exceeds 24 hours
        if (currentElapsed.hours >= 24) {
          setEatingElapsedTime({ hours: 0, minutes: 0, seconds: 0 });
          setEatingWindowStartTime(null);
          clearInterval(eatingInterval);
          return;
        }
        
        setEatingElapsedTime(currentElapsed);
      }, 1000);

      return () => {
        clearInterval(eatingInterval);
      };
    }
  }, [eatingWindowStartTime, session, calculateElapsedTime]);

  // Load initial data and subscribe to real-time updates
  useEffect(() => {
    if (!user) {
      setSession(null);
      setSettings(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Load settings and last break time
    getFastingSettings(user.uid)
      .then(setSettings)
      .catch((error) => {
        console.error('Error loading settings:', error);
        // Set default settings if loading fails
        setSettings({
          userId: user.uid,
          defaultPlan: '16:8',
          notifications: false,
          updatedAt: new Date(),
        });
      });

    // Get last break time from history
    getFastingHistory(user.uid, 1)
      .then((history) => {
        const lastSession = history.find(s => s.breakTime || s.endTime);
        if (lastSession) {
          setLastBreakTime(lastSession.breakTime || lastSession.endTime || null);
        }
      })
      .catch((error) => {
        console.error('Error loading fasting history:', error);
      });

    // Subscribe to active session real-time updates using collection query
    let unsubscribe: (() => void) | null = null;

    const setupSubscription = async () => {
      try {
        // Query for active sessions
        const q = query(
          collection(db, 'fastingSessions'),
          where('userId', '==', user.uid),
          where('status', '==', 'active'),
          orderBy('startTime', 'desc'),
          limit(1)
        );

        unsubscribe = onSnapshot(
          q,
          (snapshot) => {
            if (!snapshot.empty) {
              const doc = snapshot.docs[0];
              const data = doc.data();
              const activeSession: FastingSession = {
                id: doc.id,
                userId: data.userId,
                startTime: data.startTime.toDate(),
                endTime: data.endTime?.toDate(),
                breakTime: data.breakTime?.toDate(),
                targetHours: data.targetHours,
                status: data.status,
                createdAt: data.createdAt.toDate(),
                updatedAt: data.updatedAt.toDate(),
              };
              setSession(activeSession);
              setElapsedTime(calculateElapsedTime(activeSession.startTime));
            } else {
              setSession(null);
              setElapsedTime({ hours: 0, minutes: 0, seconds: 0 });
              // Update last break time when session ends
              getFastingHistory(user.uid, 1)
                .then((history) => {
                  const lastSession = history.find(s => s.breakTime || s.endTime);
                  if (lastSession) {
                    setLastBreakTime(lastSession.breakTime || lastSession.endTime || null);
                  }
                })
                .catch(console.error);
            }
            setLoading(false);
          },
          (error) => {
            console.error('Error in subscription:', error);
            // Fallback: direct query
            getActiveFastingSession(user.uid)
              .then((activeSession) => {
                setSession(activeSession);
                if (activeSession) {
                  setElapsedTime(calculateElapsedTime(activeSession.startTime));
                }
                setLoading(false);
              })
              .catch((err) => {
                console.error('Error fetching active session:', err);
                setLoading(false);
              });
          }
        );
      } catch (error) {
        console.error('Error setting up subscription:', error);
        // Fallback: direct query
        getActiveFastingSession(user.uid)
          .then((activeSession) => {
            setSession(activeSession);
            if (activeSession) {
              setElapsedTime(calculateElapsedTime(activeSession.startTime));
            }
            setLoading(false);
          })
          .catch((err) => {
            console.error('Error fetching active session:', err);
            setLoading(false);
          });
      }
    };

    setupSubscription();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [user, calculateElapsedTime]);

  const startFasting = useCallback(async (targetHours?: number) => {
    if (!user) throw new Error('User not authenticated');
    
    const hours = targetHours || settings?.defaultPlan ? 
      parseFastingPlan(settings?.defaultPlan || '16:8') : 16;
    
    await startFastingSession(user.uid, hours);
    // Session will update via real-time subscription
  }, [user, settings]);

  const stopFasting = useCallback(async () => {
    if (!user) throw new Error('User not authenticated');
    await stopFastingSession(user.uid);
    // Update last break time immediately
    setLastBreakTime(new Date());
    // Session will update via real-time subscription
  }, [user]);

  const updateSettings = useCallback(async (newSettings: Partial<Omit<FastingSettings, 'userId' | 'updatedAt'>>) => {
    if (!user) throw new Error('User not authenticated');
    await updateFastingSettings(user.uid, newSettings);
    const updated = await getFastingSettings(user.uid);
    setSettings(updated);
  }, [user]);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [activeSession, currentSettings] = await Promise.all([
        getActiveFastingSession(user.uid),
        getFastingSettings(user.uid),
      ]);
      setSession(activeSession);
      setSettings(currentSettings);
      if (activeSession) {
        setElapsedTime(calculateElapsedTime(activeSession.startTime));
      }
    } catch (error) {
      console.error('Error refreshing fasting data:', error);
    } finally {
      setLoading(false);
    }
  }, [user, calculateElapsedTime]);

  // Calculate progress percentage for fasting
  const progress = session && session.status === 'active' && session.targetHours > 0
    ? Math.min(100, (elapsedTime.hours + elapsedTime.minutes / 60) / session.targetHours * 100)
    : 0;

  // Calculate if currently fasting
  const isFasting = session?.status === 'active' || false;

  // Calculate eating window progress
  const eatingProgress = eatingWindowStartTime && eatingWindowHours > 0 && !isFasting
    ? Math.min(100, (eatingElapsedTime.hours + eatingElapsedTime.minutes / 60) / eatingWindowHours * 100)
    : 0;

  // Check if eating window is still active (within the eating hours)
  const isEatingWindowActive = eatingWindowStartTime && !isFasting && eatingProgress < 100;

  return {
    session,
    settings,
    elapsedHours: elapsedTime.hours,
    elapsedMinutes: elapsedTime.minutes,
    elapsedSeconds: elapsedTime.seconds,
    progress,
    isFasting,
    loading,
    lastBreakTime,
    eatingWindowStartTime,
    eatingWindowHours,
    eatingElapsedHours: eatingElapsedTime.hours,
    eatingElapsedMinutes: eatingElapsedTime.minutes,
    eatingElapsedSeconds: eatingElapsedTime.seconds,
    eatingProgress,
    startFasting,
    stopFasting,
    updateSettings,
    refresh,
  };
}

/**
 * Parse fasting plan string to hours
 * '16:8' -> 16, '18:6' -> 18, etc.
 */
function parseFastingPlan(plan: string): number {
  const match = plan.match(/^(\d+):/);
  return match ? parseInt(match[1], 10) : 16;
}

